import React from 'react'
import { Wifi, Database, Zap, BarChart2, Clock } from 'lucide-react'
import { timeAgo } from '../lib/utils'

function ServiceRow({ icon: Icon, label, status, detail }) {
  const ok      = status === 'ok' || status === 'healthy' || status === 'online' || status === 'available' || status === true
  const warn    = status === 'warn' || status === 'degraded'
  const pending  = status === 'loading'

  return (
    <div
      className="flex items-center justify-between py-2.5 last:border-0"
      style={{ borderBottom: '1px solid rgba(30,41,59,0.6)' }}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-3.5 w-3.5 text-slate-700 flex-shrink-0" />
        <span className="text-sm text-slate-400">{label}</span>
      </div>

      <div className="flex items-center gap-2.5">
        {detail && (
          <span
            className="text-slate-700 hidden sm:inline"
            style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}
          >
            {detail}
          </span>
        )}

        {pending ? <span className="dot-warn" style={{ opacity: 0.4 }} /> :
         ok      ? <span className="dot-online"  /> :
         warn    ? <span className="dot-warn"    /> :
                   <span className="dot-offline" />}

        <span
          className={`font-medium w-14 text-right tracking-wider ${
            pending ? 'text-slate-600' : ok ? 'text-emerald-500' : warn ? 'text-amber-500' : 'text-rose-500'
          }`}
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem' }}
        >
          {pending ? '···' : ok ? 'ONLINE' : warn ? 'WARN' : 'OFFLINE'}
        </span>
      </div>
    </div>
  )
}

export default function SystemHealthChart({ health, dbMetrics, loading }) {
  const dbAccess  = health?.database_access === 'available'
  const latestTs  = dbMetrics?.latest_timestamp || health?.latest_data
  const activity  = dbMetrics?.recent_activity_24h
  const sigCount  = dbMetrics?.table_counts?.active_signals
  // Until the first health/dbMetrics fetch resolves, show a neutral pending state —
  // not "OFFLINE", which reads as a real outage on every page load.
  const pend = (v) => (loading && !health ? 'loading' : v)

  // Signal engine: show WARN if no new signals have been generated in last 24h.
  // active_signals count does not change if the engine is stuck — but we can
  // at least flag if bars are flowing (activity > 0) but signal count is static.
  // A more accurate check would require a dedicated staleness endpoint.
  const dataFlowing = activity > 0

  return (
    <div className="rounded-xl border border-gray-800 p-5" style={{ background: '#111827' }}>
      <h3 className="section-header">System Status</h3>

      <div>
        <ServiceRow icon={Wifi}      label="Backend API"     status={pend(health ? 'ok' : 'error')}      detail={health?.framework} />
        <ServiceRow icon={Database}  label="Database"        status={pend(dbAccess ? 'ok' : 'error')}     detail={latestTs ? timeAgo(latestTs) : undefined} />
        <ServiceRow icon={Zap}       label="Data Collection" status={pend(dataFlowing ? 'ok' : 'warn')}   detail={activity != null ? `${activity.toLocaleString()} bars/24h` : undefined} />
        <ServiceRow icon={BarChart2} label="Signal Engine"   status={pend(health ? 'ok' : 'error')}       detail={sigCount != null ? `${sigCount.toLocaleString()} total signals` : undefined} />
        <ServiceRow icon={Clock}     label="Trailing Stops"  status={pend(health ? 'ok' : 'error')}       detail="2.5%→ATR 2.5-8% trail · 6% hard" />
      </div>

      {latestTs && (
        <div className="mt-4 pt-3" style={{ borderTop: '1px solid #1f2937' }}>
          <p style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.65rem', color: '#4b5563' }}>
            LAST DATA:{' '}
            <span style={{ color: '#94a3b8' }}>{timeAgo(latestTs)}</span>
          </p>
        </div>
      )}
    </div>
  )
}
